import React, { useEffect, useRef, useState } from "react"
import { IDataSource, IFilterStep, IJoinStep, IQuery, IQueryBuilderContext, IStepValidationResponse, StepType } from "@types"
import { findSourceById, getSelectedStepDetailsById, validateFieldConfiguration, registerStep, validateSource, validateStep, validateStepNameAndId, handleStepFieldValueChanges, handleMultipleStepFieldValueChanges } from "@utils"
import { FormSection } from "@components/FormSection/FormSection"
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput"
import { FilterConditionsEditor } from "../FilterConditions/FilterConditionsEditor"
import { getFilterConditionsFromQuery, getQueryFromFilterConditions, IFilterConditionGroup, validateFilterConditions } from "../FilterConditions/filter-condition-utils"
import Icon from '@images/filter.svg'
import { Checkbox } from "uxp/components"
import { checkStatus } from "@query-engine-service"

export function renderStep(context: IQueryBuilderContext, query: IQuery, onChange: (query: IQuery) => void, selectedStepId: string) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId)
    const _step: IFilterStep = Object.assign({}, step as IFilterStep)

    const [errors, setErrors] = useState<Record<string, string>>({})
    const [counter, setCounter] = useState(0)
    const [supportParams, setSupportParams] = useState(false)

    const hasModifiedRef = useRef(false)
    const modifiedConditionsRef = useRef<IFilterConditionGroup | null>(null)

    useEffect(() => {
        checkStatus(context)
            .then(res => {
                setSupportParams(res?.details?.supportParams || false)
            })
    }, [])


    useEffect(() => {
        const { isValid, errors } = validateStep(query, step as IFilterStep)

        if (hasModifiedRef.current) {
            errors.filter = 'You have modified the conditions, please click on Apply to apply the filters'
        }

        setErrors(errors)
    }, [step, counter])

    async function onValueChange(field: string, value: any, source?: boolean) {
        const _query = await handleStepFieldValueChanges(context, query, selectedStepId, field, value, source)
        onChange(_query)
    }

    async function applyFilterConditionChanges(conditions: IFilterConditionGroup) {
        const _query = await handleMultipleStepFieldValueChanges(context, query, selectedStepId, [
            { field: 'filter', value: getQueryFromFilterConditions(conditions) },
            { field: 'ui', value: conditions }
        ])
        onChange(_query)
        hasModifiedRef.current = false
        modifiedConditionsRef.current = null
    }

    function getFilterConditions() {
        if (modifiedConditionsRef.current) return modifiedConditionsRef.current
        if (!!_step?.ui) return _step.ui
        return getFilterConditionsFromQuery(_step?.filter)
    }


    const source = findSourceById(query, _step?.source)

    return <React.Fragment>

        <FormSection
            label='Source'
            error={errors?.source}
        >
            <SourceSelectorInput
                source={source}
                onChange={(newSource) => { onValueChange('source', newSource, true) }}
                availableSources={[...previousSteps, ...selectedSources]}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Filters'
            error={errors?.filter}
            inputMinWidth={'100%'}
        >
            {
                (supportParams)
                    ? <>
                        <br />
                        <Checkbox
                            checked={_step.ignoreFiltersIfValueIsEmpty || false}
                            onChange={v => { onValueChange('ignoreFiltersIfValueIsEmpty', v) }}
                            label="Ignore filters with empty values"
                            type="bordered"
                        />
                        <br />
                    </>
                    : null
            }
            <FilterConditionsEditor
                filterConditions={getFilterConditions()}
                onChange={(updated) => { applyFilterConditionChanges(updated) }}
                attributes={(source as IDataSource)?.schema || []}
                onChnageConfitions={(conds) => {
                    const reRender = hasModifiedRef.current == false
                    hasModifiedRef.current = true
                    modifiedConditionsRef.current = conds
                    if (reRender) setCounter(prev => prev + 1)
                }}
                query={query}
            />
        </FormSection>

    </React.Fragment>
}


function validate(query: IQuery, step: IFilterStep): IStepValidationResponse {
    const errors: Record<string, string> = validateStepNameAndId(step);

    // validate source
    const source = validateSource(query, step, 'source')
    if (!source.isValid) errors.source = source.error

    // validate filters 
    const filters = validateFilterConditions(step?.filter)
    if (filters.isValid) {
        // check if the configured fields are form the correct source
        const fields = validateFieldConfiguration(query, step, filters.uniqueAttributes, 'source')
        if (!fields.isValid) errors.filter = fields.error
    }
    else errors.filter = filters.error

    return { isValid: Object.keys(errors).length === 0, errors };
}

registerStep({
    type: StepType.Filter,
    name: "Filter",
    description: "Filter data by one or more fields",
    icon: Icon,
    iconStyles: { backgroundSize: '50%' },
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return { ...basicProps, source: previousSource, filter: {} } as IFilterStep
    },
})